import * as RadixToast from '@radix-ui/react-toast';
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { cn } from '../lib/cn.js';

export type ToastTone = 'info' | 'success' | 'warning' | 'danger';

interface ToastMessage {
  id: number;
  title: string;
  description?: string;
  tone: ToastTone;
}

interface ToastContextValue {
  show: (message: Omit<ToastMessage, 'id'> | string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const toneClasses: Record<ToastTone, string> = {
  info: 'border-brand-primary-tint-deep bg-brand-primary-tint',
  success: 'border-success-tint bg-success-tint',
  warning: 'border-warning-tint bg-warning-tint',
  danger: 'border-danger-tint bg-danger-tint',
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [messages, setMessages] = useState<ToastMessage[]>([]);

  const show = useCallback((message: Omit<ToastMessage, 'id'> | string) => {
    const normalized: Omit<ToastMessage, 'id'> =
      typeof message === 'string' ? { title: message, tone: 'info' } : message;
    setMessages((current) => [...current, { ...normalized, id: Date.now() + Math.random() }]);
  }, []);

  const value = useMemo(() => ({ show }), [show]);

  return (
    <ToastContext.Provider value={value}>
      <RadixToast.Provider swipeDirection="right" duration={6000}>
        {children}
        {messages.map((message) => (
          <RadixToast.Root
            key={message.id}
            onOpenChange={(open) => {
              if (!open) setMessages((current) => current.filter((m) => m.id !== message.id));
            }}
            className={cn('rounded-lg border p-card-compact shadow-lg', toneClasses[message.tone])}
          >
            <RadixToast.Title className="font-heading text-content font-semibold text-text-primary">
              {message.title}
            </RadixToast.Title>
            {message.description ? (
              <RadixToast.Description className="mt-1 text-small text-text-secondary">
                {message.description}
              </RadixToast.Description>
            ) : null}
          </RadixToast.Root>
        ))}
        <RadixToast.Viewport className="fixed bottom-4 right-4 z-50 flex w-[min(92vw,380px)] flex-col gap-inline outline-none" />
      </RadixToast.Provider>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast moet binnen een <ToastProvider> gebruikt worden.');
  return context;
}
